// src/pages/TermsOfService.js
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-600">
            Last Updated: November 6, 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Welcome to SkillSyncs ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the SkillSyncs platform, including our website, mobile applications, and related services (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
            </p>
            <p className="text-slate-700 leading-relaxed">
              SkillSyncs is a global technology platform that connects clients seeking technical assistance with qualified helpers who can provide expert solutions. Our Service facilitates problem-solving, knowledge sharing, and professional collaboration across various technical domains.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              2. Acceptance of Terms
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              By creating an account, accessing, or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not access or use the Service.
            </p>
            <p className="text-slate-700 leading-relaxed">
              You must be at least 18 years old and have the legal capacity to enter into binding agreements in your jurisdiction to use our Service. If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
            </p>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              3. Account Registration and Security
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">3.1 Account Creation:</strong> To use certain features of the Service, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">3.2 Email Verification:</strong> You must verify your email address to complete registration and access the full functionality of the Service. We reserve the right to suspend or terminate accounts with unverified email addresses.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">3.3 Account Security:</strong> You are solely responsible for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any other breach of security.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">3.4 Account Termination:</strong> We reserve the right to suspend or terminate your account at any time, with or without notice, for violation of these Terms or for any other reason we deem necessary to protect the integrity of the Service.
              </p>
            </div>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              4. User Responsibilities and Conduct
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">4.1 Acceptable Use:</strong> You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
                <li>Violate any applicable local, state, national, or international law or regulation</li>
                <li>Infringe upon the intellectual property rights of others</li>
                <li>Transmit any malicious code, viruses, or harmful software</li>
                <li>Attempt to gain unauthorized access to the Service or related systems</li>
                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                <li>Collect or harvest information about other users without their consent</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with any entity</li>
                <li>Engage in any fraudulent, deceptive, or misleading practices</li>
              </ul>
              <p className="leading-relaxed mt-4">
                <strong className="text-slate-900">4.2 Content Standards:</strong> All content you submit, post, or display through the Service must be accurate, lawful, and not infringe upon the rights of others. You retain ownership of content you submit but grant us a license to use, modify, and display such content as necessary to provide the Service.
              </p>
            </div>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              5. Service Description
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">5.1 Platform Functionality:</strong> SkillSyncs provides a platform for connecting clients with technical problems to qualified helpers who can provide solutions. We facilitate communication, file sharing, and collaboration through our workspace features.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">5.2 Matching Service:</strong> Our intelligent matching algorithm connects clients with helpers based on skills, expertise, availability, and other relevant factors. We do not guarantee specific outcomes or results.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">5.3 Service Modifications:</strong> We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice. We are not liable for any loss or damage resulting from such modifications.
              </p>
            </div>
          </section>

          {/* Client Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              6. Client Responsibilities
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">6.1 Problem Submission:</strong> Clients are responsible for providing accurate and complete information when submitting problems. You must clearly describe your technical issue and provide all necessary context and relevant files.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">6.2 Communication:</strong> Clients must maintain professional and respectful communication with helpers. Timely responses and clear feedback are essential for successful problem resolution.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">6.3 Acceptance of Solutions:</strong> Clients are responsible for reviewing and testing solutions provided by helpers. We are not responsible for the quality, accuracy, or effectiveness of solutions provided by helpers.
              </p>
            </div>
          </section>

          {/* Helper Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              7. Helper Responsibilities
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">7.1 Qualifications:</strong> Helpers must accurately represent their skills, experience, and qualifications. You must only accept problems that you are qualified to solve.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">7.2 Professional Conduct:</strong> Helpers must provide professional, accurate, and timely assistance. You must maintain confidentiality regarding client information and problem details.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">7.3 Solution Quality:</strong> Helpers are responsible for the quality and accuracy of solutions provided. You must test solutions when possible and provide clear documentation and explanations.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">7.4 Availability:</strong> Helpers must accurately represent their availability and respond to client communications in a timely manner.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              8. Intellectual Property Rights
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">8.1 Service Ownership:</strong> The Service, including its original content, features, and functionality, is owned by SkillSyncs and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">8.2 User Content:</strong> You retain ownership of content you submit to the Service. By submitting content, you grant SkillSyncs a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and distribute your content solely for the purpose of providing and improving the Service.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">8.3 Trademarks:</strong> The SkillSyncs name, logo, and related marks are trademarks of SkillSyncs. You may not use these marks without our prior written permission.
              </p>
            </div>
          </section>

          {/* Collaboration Standards */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              9. Collaboration Standards
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">9.1 Transparent Communication:</strong> Share accurate status updates, ask clarifying questions, and keep all workspace conversations professional.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">9.2 Scope Alignment:</strong> Confirm goals, deliverables, and timelines before beginning work. Any scope change should be discussed openly between participants.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">9.3 Respectful Conduct:</strong> Treat every collaborator with courtesy. Harassment, discrimination, or abusive behavior is grounds for removal from the platform.
              </p>
            </div>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              10. Disclaimers and Limitations of Liability
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">10.1 Service "As Is":</strong> The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or secure.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">10.2 No Guarantee of Results:</strong> We do not guarantee that problems will be solved or that solutions will meet your expectations. The quality and effectiveness of solutions depend on the helpers and the information provided.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">10.3 Limitation of Liability:</strong> To the maximum extent permitted by law, SkillSyncs shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">10.4 Maximum Liability:</strong> Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              11. Indemnification
            </h2>
            <p className="text-slate-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless SkillSyncs, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal expenses, arising out of or in any way connected with your access to or use of the Service, your violation of these Terms, or your infringement of any rights of another.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              12. Dispute Resolution
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">12.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which SkillSyncs operates, without regard to its conflict of law provisions.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">12.2 Arbitration:</strong> Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of a recognized arbitration organization, except where prohibited by law.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">12.3 Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              13. Changes to Terms
            </h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms. If you do not agree to the modified Terms, you must stop using the Service.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              14. Termination
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">14.1 Termination by You:</strong> You may terminate your account at any time by contacting us or using the account deletion features in the Service.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">14.2 Termination by Us:</strong> We may terminate or suspend your account and access to the Service immediately, without prior notice, for any breach of these Terms or for any other reason we deem necessary.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">14.3 Effect of Termination:</strong> Upon termination, your right to use the Service will immediately cease. Provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
              </p>
            </div>
          </section>

          {/* General Provisions */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              15. General Provisions
            </h2>
            <div className="space-y-3 text-slate-700">
              <p className="leading-relaxed">
                <strong className="text-slate-900">15.1 Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and SkillSyncs regarding the Service.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">15.2 Severability:</strong> If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">15.3 Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">15.4 Assignment:</strong> You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms without restriction.
              </p>
              <p className="leading-relaxed">
                <strong className="text-slate-900">15.5 Contact Information:</strong> If you have any questions about these Terms, please contact us at skillsyncs@outlook.com.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="pt-8 border-t border-slate-200">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              Contact Us
            </h2>
            <p className="text-slate-700 leading-relaxed">
              If you have any questions, concerns, or requests regarding these Terms of Service, please contact us:
            </p>
            <div className="mt-4 text-slate-700">
              <p><strong>Email:</strong> skillsyncs@outlook.com</p>
              <p className="mt-2"><strong>Address:</strong> SkillSyncs Global Services</p>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-8 flex justify-between items-center">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 transition"
          >
            ← Back to Home
          </Link>
          <Link
            to="/privacy"
            className="text-blue-600 hover:text-blue-700 transition"
          >
            Privacy Policy →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;

